import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown, User, Home, Package, Boxes as BoxesIcon } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { NAV_CATEGORIES, BOX_LINKS } from "../lib/categories";
import logo from "../assets/afrigate-logo.png";

export function Navbar() {
  const { count } = useCart();
  const { primary, setPrimary } = useCurrency();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null); // 'shop' | 'boxes' | null
  const navigate = useNavigate();
  const isCustomer = user && user.email;

  const linkCls = "flex items-center gap-1 text-sm font-medium text-[#0D530E] hover:text-[#306D29]";

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FBF5DD]/80 border-b border-[#0D530E]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[92px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src={logo} alt="AfriGate" className="h-14 sm:h-[72px] w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <div className="relative" onMouseEnter={() => setMega("shop")} onMouseLeave={() => setMega(null)}>
              <button data-testid="nav-shop" className={linkCls} style={{ transition: "color 0.2s ease" }}>
                Shop <ChevronDown size={15} className={mega === "shop" ? "rotate-180" : ""} style={{ transition: "transform 0.2s ease" }} />
              </button>
              {mega === "shop" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4">
                  <div className="w-[560px] bg-[#FBF5DD] rounded-2xl border border-[#0D530E]/10 shadow-[0_20px_60px_rgba(13,83,14,0.15)] p-4 grid grid-cols-2 gap-1" data-testid="mega-menu">
                    {NAV_CATEGORIES.map((c) => (
                      <Link key={c.name} to={`/category/${encodeURIComponent(c.name)}`} data-testid={`mega-${c.name}`}
                        className="flex items-center gap-3 rounded-xl p-3 hover:bg-[#E7E1B1]/50" style={{ transition: "background-color 0.2s ease" }}>
                        <span className="w-11 h-11 rounded-lg bg-[#306D29]/10 text-[#306D29] flex items-center justify-center shrink-0"><c.Icon size={20} /></span>
                        <div><p className="font-medium text-[#0D530E] text-sm">{c.label}</p><p className="text-xs text-[#306D29]/70">{c.blurb}</p></div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" onMouseEnter={() => setMega("boxes")} onMouseLeave={() => setMega(null)}>
              <button data-testid="nav-boxes" onClick={() => navigate("/boxes")} className={linkCls} style={{ transition: "color 0.2s ease" }}>
                Boxes <ChevronDown size={15} className={mega === "boxes" ? "rotate-180" : ""} style={{ transition: "transform 0.2s ease" }} />
              </button>
              {mega === "boxes" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4">
                  <div className="w-64 bg-[#FBF5DD] rounded-2xl border border-[#0D530E]/10 shadow-[0_20px_60px_rgba(13,83,14,0.15)] p-3">
                    <p className="text-xs text-[#306D29]/70 px-2 pb-2">Buy once or subscribe monthly</p>
                    {BOX_LINKS.map((b) => (
                      <Link key={b} to="/boxes" className="block rounded-xl px-3 py-2 text-sm font-medium text-[#0D530E] hover:bg-[#E7E1B1]/50" style={{ transition: "background-color 0.2s ease" }}>{b}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/how-it-works" data-testid="nav-how" className={linkCls} style={{ transition: "color 0.2s ease" }}>How It Works</Link>
            <Link to="/track" data-testid="nav-track" className={linkCls} style={{ transition: "color 0.2s ease" }}>Track Order</Link>
            <Link to="/support" data-testid="nav-support" className={linkCls} style={{ transition: "color 0.2s ease" }}>Support</Link>
          </nav>

          <div className="flex items-center gap-2.5">
            <div className="hidden sm:flex items-center rounded-full border border-[#0D530E]/15 p-0.5 text-xs font-semibold">
              {["GHS", "GBP"].map((c) => (
                <button key={c} data-testid={`currency-${c}`} onClick={() => setPrimary(c)}
                  className={`px-3 py-1 rounded-full ${primary === c ? "bg-[#306D29] text-[#FBF5DD]" : "text-[#306D29]"}`} style={{ transition: "background-color 0.2s ease" }}>
                  {c === "GHS" ? "GH₵" : "£"}
                </button>
              ))}
            </div>
            <button data-testid="nav-account" onClick={() => navigate(isCustomer ? "/account" : "/login")}
              className="hidden lg:flex w-10 h-10 rounded-full border border-[#0D530E]/15 items-center justify-center hover:border-[#306D29] text-[#0D530E]" style={{ transition: "border-color 0.2s ease" }}>
              <User size={18} aria-hidden="true" />
            </button>
            <button data-testid="nav-cart" onClick={() => navigate("/cart")}
              className="relative hidden lg:flex w-10 h-10 rounded-full border border-[#0D530E]/15 items-center justify-center hover:border-[#306D29] text-[#0D530E]" style={{ transition: "border-color 0.2s ease" }}>
              <ShoppingBag size={18} aria-hidden="true" />
              {count > 0 && <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 bg-[#306D29] text-[#FBF5DD] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
            </button>
            <button className="lg:hidden w-10 h-10 rounded-full border border-[#0D530E]/15 flex items-center justify-center text-[#0D530E]" data-testid="nav-menu-toggle" onClick={() => setOpen(!open)}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#0D530E]/10 bg-[#FBF5DD] px-5 py-4 flex flex-col gap-1">
            {NAV_CATEGORIES.map((c) => (
              <Link key={c.name} to={`/category/${encodeURIComponent(c.name)}`} onClick={() => setOpen(false)} className="flex items-center gap-2 text-sm font-medium py-2 text-[#0D530E]"><c.Icon size={16} className="text-[#306D29]" /> {c.label}</Link>
            ))}
            <div className="h-px bg-[#0D530E]/10 my-2" />
            <Link to="/boxes" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-[#0D530E]">Boxes</Link>
            <Link to="/how-it-works" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-[#0D530E]">How It Works</Link>
            <Link to="/track" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-[#0D530E]">Track Order</Link>
            <Link to="/support" onClick={() => setOpen(false)} className="text-sm font-medium py-2 text-[#0D530E]">Support</Link>
          </div>
        )}
      </header>

      <MobileBottomNav isCustomer={isCustomer} />
    </>
  );
}

function MobileBottomNav({ isCustomer }) {
  const location = useLocation();
  const { count } = useCart();
  const items = [
    { to: "/", label: "Home", icon: Home },
    { to: "/shop", label: "Shop", icon: Package },
    { to: "/boxes", label: "Boxes", icon: BoxesIcon },
    { to: "/cart", label: "Cart", icon: ShoppingBag, badge: count },
    { to: isCustomer ? "/account" : "/login", label: "Account", icon: User },
  ];
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#FBF5DD]/95 backdrop-blur-xl border-t border-[#0D530E]/10 flex" data-testid="mobile-bottom-nav">
      {items.map((it, i) => {
        const active = location.pathname === it.to;
        return (
          <Link key={i} to={it.to} data-testid={`bottomnav-${it.label.toLowerCase()}`} className={`relative flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${active ? "text-[#306D29]" : "text-[#0D530E]/60"}`}>
            <span className="relative">
              <it.icon size={19} />
              {it.badge > 0 && <span className="absolute -top-1.5 -right-2 bg-[#306D29] text-[#FBF5DD] text-[9px] font-bold min-w-[15px] h-[15px] px-1 rounded-full flex items-center justify-center">{it.badge}</span>}
            </span>
            {it.label}
          </Link>
        );
      })}
    </nav>
  );
}
